import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/utils/api';
import { 
  ArrowLeft, Save, Eye, Loader2, Image as ImageIcon, 
  Type, FileText, Settings, Globe, Plus, Trash2, GripVertical
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section types available
const SECTION_TYPES = [
  { value: 'text', label: 'Texte', icon: Type },
  { value: 'cta', label: 'Appel à l\'action', icon: FileText },
  { value: 'image', label: 'Image', icon: ImageIcon },
  { value: 'features', label: 'Liste de fonctionnalités', icon: Settings },
  { value: 'testimonials', label: 'Témoignages', icon: Globe },
];

// Page types
const PAGE_TYPES = [
  { value: 'landing', label: 'Landing Page' },
  { value: 'service', label: 'Page Service' },
  { value: 'promo', label: 'Page Promotion' },
  { value: 'article', label: 'Article' },
];

// Parent services
const PARENT_SERVICES = [
  { value: 'none', label: 'Aucun (page indépendante)' },
  { value: 'nettoyage', label: 'Nettoyage' },
  { value: 'renovation', label: 'Rénovation' },
  { value: 'conciergerie', label: 'Conciergerie' },
  { value: 'montage', label: 'Montage' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'immo', label: 'Immobilier' },
];

export const AdminCMSEditor = () => {
  const navigate = useNavigate();
  const { pageId } = useParams();
  const isEditing = !!pageId;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    slug: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    page_type: 'landing',
    parent_service: 'none',
    hero_title: '',
    hero_subtitle: '',
    hero_image_url: '',
    hero_cta_text: 'Demander un devis',
    hero_cta_link: '#form',
    sections: [],
    show_form: true,
    form_title: 'Demandez votre devis gratuit',
    form_questions: [],
    is_published: false,
    is_featured: false,
  });

  useEffect(() => {
    checkSession();
    if (isEditing) {
      loadPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  const checkSession = async () => {
    try {
      await api.get(`${API_BASE}/admin/me`, { withCredentials: true });
    } catch (error) {
      console.error('Session check failed:', error);
      navigate('/admin/login');
    }
  };

  const loadPage = async () => {
    setLoading(true);
    try {
      const response = await api.get(`${API_BASE}/admin/cms/pages/${pageId}`, {
        withCredentials: true
      });
      const data = response.data;
      setFormData({
        slug: data.slug || '',
        meta_title: data.meta_title || '',
        meta_description: data.meta_description || '',
        meta_keywords: data.meta_keywords || '',
        page_type: data.page_type || 'landing',
        parent_service: data.parent_service || 'none',
        hero_title: data.hero_title || '',
        hero_subtitle: data.hero_subtitle || '',
        hero_image_url: data.hero_image_url || '',
        hero_cta_text: data.hero_cta_text || 'Demander un devis',
        hero_cta_link: data.hero_cta_link || '#form',
        sections: data.sections || [],
        show_form: data.show_form ?? true,
        form_title: data.form_title || 'Demandez votre devis gratuit',
        form_questions: data.form_questions || [],
        is_published: data.is_published || false,
        is_featured: data.is_featured || false,
      });
    } catch (error) {
      console.error('Error loading page:', error);
      toast.error('Erreur lors du chargement de la page');
      navigate('/admin/cms');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (publish = false) => {
    // Validation
    if (!formData.slug || !formData.meta_title || !formData.hero_title) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Validate slug format
    if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      toast.error('Le slug ne doit contenir que des lettres minuscules, chiffres et tirets');
      return;
    }

    setSaving(true);
    
    const dataToSend = {
      ...formData,
      is_published: publish ? true : formData.is_published,
      parent_service: formData.parent_service === 'none' ? null : formData.parent_service,
    };

    try {
      if (isEditing) {
        await api.put(`${API_BASE}/admin/cms/pages/${pageId}`, dataToSend, {
          withCredentials: true
        });
        toast.success(publish ? 'Page publiée !' : 'Page mise à jour');
      } else {
        await api.post(`${API_BASE}/admin/cms/pages`, dataToSend, {
          withCredentials: true
        });
        toast.success(publish ? 'Page créée et publiée !' : 'Page créée');
        navigate('/admin/cms');
      }
      
      if (publish && !formData.is_published) {
        setFormData({ ...formData, is_published: true });
      }
    } catch (error) {
      console.error('Error saving page:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
      .replace(/^-+|-+$/g, ''); // Trim hyphens
  };

  // Section management
  const addSection = (type) => {
    const newSection = {
      id: `section-${Date.now()}`,
      type,
      title: '',
      subtitle: '',
      content: '',
      image_url: '',
      cta_text: '',
      cta_link: '',
      background_color: '',
      extra_data: {},
      order: formData.sections.length,
    };
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
  };

  const updateSection = (sectionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, [field]: value } : s
      )
    }));
  };

  const removeSection = (sectionId) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId)
    }));
  };

  // Form questions management
  const addFormQuestion = () => {
    const newQuestion = {
      id: `q-${Date.now()}`,
      label: '',
      type: 'text',
      placeholder: '',
      options: [],
      required: false,
    };
    setFormData(prev => ({
      ...prev,
      form_questions: [...prev.form_questions, newQuestion]
    }));
  };

  const updateFormQuestion = (questionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      form_questions: prev.form_questions.map(q => 
        q.id === questionId ? { ...q, [field]: value } : q
      )
    }));
  };

  const removeFormQuestion = (questionId) => {
    setFormData(prev => ({
      ...prev,
      form_questions: prev.form_questions.filter(q => q.id !== questionId)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/cms')}
            data-testid="back-to-cms-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEditing ? 'Modifier la page' : 'Nouvelle page'}
            </h1>
            {formData.slug && (
              <p className="text-sm text-muted-foreground">
                URL : /p/{formData.slug}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {formData.is_published && (
            <Button
              variant="outline"
              onClick={() => window.open(`/p/${formData.slug}`, '_blank')}
            >
              <Eye className="mr-2 h-4 w-4" />
              Voir
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => handleSubmit(false)}
            disabled={saving}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="mr-2 h-4 w-4" />
            Enregistrer brouillon
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            disabled={saving}
            style={{ backgroundColor: 'var(--cta-blue)' }}
            data-testid="publish-page-btn"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {formData.is_published ? 'Mettre à jour' : 'Publier'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="content" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="content">Contenu</TabsTrigger>
          <TabsTrigger value="sections">Sections</TabsTrigger>
          <TabsTrigger value="form">Formulaire</TabsTrigger>
          <TabsTrigger value="seo">SEO & Options</TabsTrigger>
        </TabsList>

        {/* Content Tab */}
        <TabsContent value="content" className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Informations de base</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="meta_title">Titre de la page *</Label>
                  <Input
                    id="meta_title"
                    value={formData.meta_title}
                    onChange={(e) => {
                      updateField('meta_title', e.target.value);
                      if (!isEditing && !formData.slug) {
                        updateField('slug', generateSlug(e.target.value));
                      }
                    }}
                    placeholder="Ex: Offre spéciale nettoyage printemps"
                    maxLength={100}
                    data-testid="meta-title-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{formData.meta_title.length}/100</p>
                </div>
                <div>
                  <Label htmlFor="slug">Slug (URL) *</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">/p/</span>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => updateField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                      placeholder="offre-nettoyage-printemps"
                      data-testid="slug-input"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="page_type">Type de page</Label>
                  <Select 
                    value={formData.page_type} 
                    onValueChange={(v) => updateField('page_type', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="parent_service">Service parent</Label>
                  <Select 
                    value={formData.parent_service} 
                    onValueChange={(v) => updateField('parent_service', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PARENT_SERVICES.map(service => (
                        <SelectItem key={service.value} value={service.value}>
                          {service.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Hero Section */}
          <Card>
            <CardHeader>
              <CardTitle>Section Hero</CardTitle>
              <CardDescription>La première section visible de votre page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="hero_title">Titre principal *</Label>
                <Input
                  id="hero_title"
                  value={formData.hero_title}
                  onChange={(e) => updateField('hero_title', e.target.value)}
                  placeholder="Ex: Profitez de -20% sur le nettoyage de printemps"
                  maxLength={200}
                  data-testid="hero-title-input"
                />
              </div>

              <div>
                <Label htmlFor="hero_subtitle">Sous-titre</Label>
                <Textarea
                  id="hero_subtitle"
                  value={formData.hero_subtitle}
                  onChange={(e) => updateField('hero_subtitle', e.target.value)}
                  placeholder="Description courte qui accompagne le titre..."
                  rows={2}
                  maxLength={500}
                />
              </div>

              <div>
                <Label htmlFor="hero_image_url">URL de l'image de fond</Label>
                <Input
                  id="hero_image_url"
                  value={formData.hero_image_url}
                  onChange={(e) => updateField('hero_image_url', e.target.value)}
                  placeholder="https://images.unsplash.com/..."
                />
                {formData.hero_image_url && (
                  <div className="mt-2 rounded-lg overflow-hidden h-32 bg-gray-100">
                    <img 
                      src={formData.hero_image_url} 
                      alt="Hero preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="hero_cta_text">Texte du bouton</Label>
                  <Input
                    id="hero_cta_text"
                    value={formData.hero_cta_text}
                    onChange={(e) => updateField('hero_cta_text', e.target.value)}
                    placeholder="Demander un devis"
                  />
                </div>
                <div>
                  <Label htmlFor="hero_cta_link">Lien du bouton</Label>
                  <Input
                    id="hero_cta_link"
                    value={formData.hero_cta_link}
                    onChange={(e) => updateField('hero_cta_link', e.target.value)}
                    placeholder="#form ou /contact"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sections Tab */}
        <TabsContent value="sections" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Sections de contenu</CardTitle>
                  <CardDescription>
                    Ajoutez des sections pour enrichir votre page
                  </CardDescription>
                </div>
                <Select onValueChange={addSection}>
                  <SelectTrigger className="w-[200px]">
                    <Plus className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Ajouter une section" />
                  </SelectTrigger>
                  <SelectContent>
                    {SECTION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {formData.sections.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune section ajoutée</p>
                  <p className="text-sm">Utilisez le menu ci-dessus pour ajouter des sections</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.sections.map((section, index) => (
                    <Card key={section.id} className="border-dashed">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
                            <span className="font-medium">
                              Section {index + 1}: {SECTION_TYPES.find(t => t.value === section.type)?.label}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSection(section.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <Label>Titre</Label>
                          <Input
                            value={section.title || ''}
                            onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                            placeholder="Titre de la section"
                          />
                        </div>
                        {section.type === 'text' && (
                          <div>
                            <Label>Contenu</Label>
                            <Textarea
                              value={section.content || ''}
                              onChange={(e) => updateSection(section.id, 'content', e.target.value)}
                              placeholder="Contenu de la section..."
                              rows={4}
                            />
                          </div>
                        )}
                        {section.type === 'image' && (
                          <div>
                            <Label>URL de l'image</Label>
                            <Input
                              value={section.image_url || ''}
                              onChange={(e) => updateSection(section.id, 'image_url', e.target.value)}
                              placeholder="https://..."
                            />
                          </div>
                        )}
                        {section.type === 'cta' && (
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Texte du bouton</Label>
                              <Input
                                value={section.cta_text || ''}
                                onChange={(e) => updateSection(section.id, 'cta_text', e.target.value)}
                                placeholder="Découvrir"
                              />
                            </div>
                            <div>
                              <Label>Lien</Label>
                              <Input
                                value={section.cta_link || ''}
                                onChange={(e) => updateSection(section.id, 'cta_link', e.target.value)}
                                placeholder="/contact"
                              />
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Form Tab */}
        <TabsContent value="form" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Formulaire de contact</CardTitle>
                  <CardDescription>
                    Configurez le formulaire de capture de leads
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="show_form">Afficher le formulaire</Label>
                  <Switch
                    id="show_form"
                    checked={formData.show_form}
                    onCheckedChange={(v) => updateField('show_form', v)}
                  />
                </div>
              </div>
            </CardHeader>
            {formData.show_form && (
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="form_title">Titre du formulaire</Label>
                  <Input
                    id="form_title"
                    value={formData.form_title}
                    onChange={(e) => updateField('form_title', e.target.value)}
                    placeholder="Demandez votre devis gratuit"
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <Label>Questions personnalisées</Label>
                  <Button variant="outline" size="sm" onClick={addFormQuestion}>
                    <Plus className="mr-2 h-4 w-4" />
                    Ajouter une question
                  </Button>
                </div>

                <p className="text-sm text-muted-foreground">
                  Les champs Nom, Email et Téléphone sont inclus automatiquement.
                </p>

                {formData.form_questions.map((question, index) => (
                  <Card key={question.id} className="border-dashed">
                    <CardContent className="pt-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Question {index + 1}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFormQuestion(question.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Label</Label>
                          <Input
                            value={question.label || ''}
                            onChange={(e) => updateFormQuestion(question.id, 'label', e.target.value)}
                            placeholder="Ex: Type de bien"
                          />
                        </div>
                        <div>
                          <Label>Type</Label>
                          <Select 
                            value={question.type || 'text'} 
                            onValueChange={(v) => updateFormQuestion(question.id, 'type', v)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Texte</SelectItem>
                              <SelectItem value="select">Liste déroulante</SelectItem>
                              <SelectItem value="textarea">Zone de texte</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {question.type === 'select' && (
                        <div>
                          <Label>Options (une par ligne)</Label>
                          <Textarea
                            value={(question.options || []).join('\n')}
                            onChange={(e) => updateFormQuestion(question.id, 'options', e.target.value.split('\n').filter(Boolean))}
                            placeholder="Appartement&#10;Maison&#10;Chalet"
                            rows={3}
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            )}
          </Card>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>SEO & Métadonnées</CardTitle>
              <CardDescription>Optimisez votre page pour les moteurs de recherche</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="meta_description">Meta Description</Label>
                <Textarea
                  id="meta_description"
                  value={formData.meta_description}
                  onChange={(e) => updateField('meta_description', e.target.value)}
                  placeholder="Description qui apparaîtra dans les résultats de recherche..."
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground mt-1">{formData.meta_description.length}/160 recommandés</p>
              </div>

              <div>
                <Label htmlFor="meta_keywords">Mots-clés (séparés par virgules)</Label>
                <Input
                  id="meta_keywords"
                  value={formData.meta_keywords}
                  onChange={(e) => updateField('meta_keywords', e.target.value)}
                  placeholder="nettoyage, lausanne, promotion"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Options de publication</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="is_published">Publier la page</Label>
                  <p className="text-sm text-muted-foreground">
                    La page sera accessible publiquement
                  </p>
                </div>
                <Switch
                  id="is_published"
                  checked={formData.is_published}
                  onCheckedChange={(v) => updateField('is_published', v)}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="is_featured">Mettre en avant</Label>
                  <p className="text-sm text-muted-foreground">
                    Afficher dans les sections "À la une"
                  </p>
                </div>
                <Switch
                  id="is_featured"
                  checked={formData.is_featured}
                  onCheckedChange={(v) => updateField('is_featured', v)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
